import type { UserInfoProps } from "../slices/authSlice";
import type { TNotes } from "../types/index.type";
import { NoteSection } from "../utils/analytics";

export enum NoteType {
  SOAP = 0,
  Intake = 1,
  DAP = 2,
  EMDR = 3,
  EAP_INTAKE = 4,
  PSYCHIATRIC_INTAKE = 5,
  SOL_PSYCH_INTAKE = 6,
  PSYCHIATRIC_FOLLOW_UP = 7,
  THERAPY_INTAKE = 8,
  THERAPY_FOLLOW_UP_SOAP = 9,
  THERAPY_FOLLOW_UP_DAP = 10,
  PSYCHIATRIC_FOLLOW_UP_SOAP = 11,
  SOAP_SPEECH_THERAPY = 12,
  CONSULT_NOTE = 13,
  // Read-only note imported from the practice's EHR by the morning patient pull.
  // Mirrors backend notes.models.EHR_PRIOR_NOTE_TYPE — deliberately far above the
  // CustomNote.type_id range (14..~10500) so it can never collide with a template.
  EHR_PRIOR_NOTE = 99999999,
}

export enum VisitType {
  TYPE_INTAKE = 0,
  TYPE_FOLLOWUP = 1,
  TYPE_OTHER = 2,
  TYPE_TREATMENT_PLAN = 3,
}

const psychiatryCptCodes = [
  { label: "99202", desc: "(New patient, 15-29 minutes)", value: 3 },
  { label: "99203", desc: "(New patient, 30-44 minutes)", value: 4 },
  { label: "99204", desc: "(New patient, 45-59 minutes)", value: 5 },
  { label: "99205", desc: "(New patient, 60-74 minutes)", value: 6 },
  {
    label: "99212",
    desc: "(Established Patient, 10-19 minutes)",
    value: 7,
  },
  {
    label: "99213",
    desc: "(Established Patient, 20-29 minutes)",
    value: 8,
  },
  {
    label: "99214",
    desc: "(Established Patient, 30-39 minutes)",
    value: 9,
  },
  {
    label: "99215",
    desc: "(Established Patient Visit, 40-54 minutes)",
    value: 10,
  },
  {
    label: "90791",
    desc: "(Psychiatric Diagnostic Evaluation)",
    value: 11,
  },
  {
    label: "90792",
    desc: "(Psychiatric Diagnostic Evaluation Including Medical Services)",
    value: 12,
  },
];

export const cptCodeOptions = [
  { label: "---", value: null },
  {
    label: <span style={{ fontWeight: "bold" }}>Psychotherapy</span>,
    title: "Psychotherapy",
    options: [
      {
        label: "90832",
        desc: "(16-37 minutes psychotherapy session)",
        value: 0,
      },
      {
        label: "90834",
        desc: "(38-52 minutes psychotherapy session)",
        value: 1,
      },
      { label: "90837", desc: "(53+ minutes psychotherapy session)", value: 2 },
      {
        label: "90839",
        desc: "(Psychotherapy for crisis situations, first 60 minutes)",
        value: 16,
      },
      {
        label: "90846",
        desc: "(Family Psychotherapy without patient present, 50 minutes)",
        value: 17,
      },
      {
        label: "90847",
        desc: "(Family Psychotherapy with patient present, 50 minutes)",
        value: 18,
      },
      {
        label: "90853",
        desc: "(Group Psychotherapy, not family-related)",
        value: 19,
      },
      {
        label: "90791",
        desc: "(Psychiatric Diagnostic Evaluation)",
        value: 11,
      },
    ],
  },
  {
    label: <span style={{ fontWeight: "bold" }}>Psychiatry</span>,
    title: "Psychiatry",
    options: psychiatryCptCodes,
  },
];

export const cptAddOnCodeOptions = [
  { label: "---", value: null },
  {
    label: <span style={{ fontWeight: "bold" }}>Add-ons</span>,
    title: "Psychotherapy Add-ons",
    options: [
      {
        label: "90833",
        desc: "Psychotherapy, 16-37 minutes",
        value: 20,
      },
      {
        label: "90836",
        desc: "Psychotherapy, 38-52 minutes",
        value: 21,
      },
      {
        label: "90838",
        desc: "Psychotherapy, 53 minutes or more",
        value: 22,
      },
    ],
  },
];

const cptCodesByPsychotherapyMinutes = [
  {
    id: 90833,
    label: "90833: Psychotherapy, 16-37 minutes",
    min: 16,
    max: 37,
    value: 20,
  },
  {
    id: 90836,
    label: "90836: Psychotherapy, 38-52 minutes",
    min: 38,
    max: 52,
    value: 21,
  },
  {
    id: 90838,
    label: "90838: Psychotherapy, 53 minutes or more",
    min: 53,
    max: Number.POSITIVE_INFINITY, // Represents 53 minutes or more
    value: 22,
  },
];

export const pyschiatryBaseCodes = () => {
  const psychiatryOptions = cptCodeOptions.find((option) => option.title === "Psychiatry")?.options;

  return psychiatryOptions?.map((opt) => opt.value);
};

export function showAddon(recordingDetails: TNotes): boolean | undefined {
  return (
    [
      NoteType.SOL_PSYCH_INTAKE,
      NoteType.PSYCHIATRIC_FOLLOW_UP,
      NoteType.PSYCHIATRIC_INTAKE,
      NoteType.PSYCHIATRIC_FOLLOW_UP_SOAP,
    ].includes(recordingDetails?.note_type) ||
    (recordingDetails?.note_type > 13 && recordingDetails?.psychotherapy_minutes !== null)
  );
}

export function showCptCodes(recordingDetails: TNotes): boolean | undefined {
  return (
    [
      NoteType.SOL_PSYCH_INTAKE,
      NoteType.PSYCHIATRIC_FOLLOW_UP,
      NoteType.PSYCHIATRIC_INTAKE,
      NoteType.PSYCHIATRIC_FOLLOW_UP_SOAP,
    ].includes(recordingDetails?.note_type) ||
    (recordingDetails?.note_type > 13 && recordingDetails?.suggested_cpt_type !== null)
  );
}

export function getCptSuggestedCode(recordingDetails?: TNotes): string {
  const cptCode = psychiatryCptCodes.find((option) => recordingDetails?.suggested_cpt_type === option.value);

  if (cptCode) {
    return `${cptCode.label}: ${cptCode.desc}`;
  }
  return "--";
}

export function getCptCodeLabelByPsychotherapyMinutes(psychotherapyMinutes: number, recordingDetails?: TNotes): string {
  if (recordingDetails?.duration_addon_type) {
    return (
      cptCodesByPsychotherapyMinutes.find((option) => option.value === recordingDetails.duration_addon_type)?.label ||
      "90833: Psychotherapy, 16-37 minutes"
    );
  }
  const cptCode = cptCodesByPsychotherapyMinutes.find(
    (option) => psychotherapyMinutes >= option.min && psychotherapyMinutes <= option.max,
  );

  return cptCode?.label || "90833: Psychotherapy, 16-37 minutes";
}

export function getCptCodeByPsychotherapyMinutes(psychotherapyMinutes: number): number | undefined {
  const cptCode = cptCodesByPsychotherapyMinutes.find(
    (option) => psychotherapyMinutes >= option.min && psychotherapyMinutes <= option.max,
  );

  return cptCode?.id;
}

export function totalTimeSpentOnPsychotherapy(text: string): number {
  // This is a hack to get the psychotherapy minutes from the note
  // These needs to be fixed on the backend - this is fixed on the backend now but using this handle the existing notes with add-ons
  const pattern = /Total Time Spent on Psychotherapy:\s*(\d+)\s*minutes?/i;

  const match = text.match(pattern);

  if (match) {
    return Number.parseInt(match[1], 10);
  }
  return 0;
}

export function extractPsychotherapyExplanation(input: string): string {
  // This is required because of the bad data i.e, for the notes that were created when the backend was saving the explanation on audiofile object
  const startIndex = input.indexOf("Psychotherapy Employed");

  if (startIndex === -1) {
    return "";
  }

  const contentStart = input.indexOf("\n", startIndex) + 1;

  const nextSection = input.indexOf("\n\n", contentStart);
  const endIndex = nextSection !== -1 ? nextSection : input.length;

  return input.slice(contentStart, endIndex).trim();
}

export const getNotesType = (note_type: NoteType) => {
  const NOTE_TYPES: Record<NoteType, string> = {
    [NoteType.SOAP]: "SOAP",
    [NoteType.Intake]: "Intake",
    [NoteType.DAP]: "DAP",
    [NoteType.EMDR]: "EMDR",
    [NoteType.EAP_INTAKE]: "EAP Intake",
    [NoteType.PSYCHIATRIC_INTAKE]: "Psychiatric Intake",
    [NoteType.SOL_PSYCH_INTAKE]: "Psychiatric Intake", // Custom Psych Intake template for SOL
    [NoteType.PSYCHIATRIC_FOLLOW_UP]: "Psychiatric Follow-p",
    [NoteType.THERAPY_INTAKE]: "Therapy Intake",
    [NoteType.THERAPY_FOLLOW_UP_SOAP]: "Therapy Followup (SOAP)",
    [NoteType.THERAPY_FOLLOW_UP_DAP]: "Therapy Followup (DAP)",
    [NoteType.PSYCHIATRIC_FOLLOW_UP_SOAP]: "Psychiatric Followup (SOAP)",
    [NoteType.SOAP_SPEECH_THERAPY]: "SOAP (Speech Therapy)",
    [NoteType.CONSULT_NOTE]: "Consult Note",
    // EHR-agnostic on purpose: one sentinel serves eCW, NextGen and whatever comes
    // next. The note TITLE names the specific system.
    [NoteType.EHR_PRIOR_NOTE]: "From EHR",
  };

  return NOTE_TYPES[note_type] || "";
};

export const getNotesHTMLStringFromItem = (item: TNotes) => {
  switch (item.note_type) {
    case NoteType.SOAP:
      return item.subject;
    case NoteType.Intake:
      return item.intake_note;
    case NoteType.DAP:
      return item.data;
    case NoteType.EMDR:
      return item.emdr_note;
    case NoteType.EAP_INTAKE:
      return item.eap_intake_note;
    case NoteType.PSYCHIATRIC_INTAKE:
      return item.psych_intake_note;
    case NoteType.SOL_PSYCH_INTAKE:
      return item.sol_psych_intake_note ?? "";
    case NoteType.PSYCHIATRIC_FOLLOW_UP:
      return item.sol_psych_followup_note ?? "";
    case NoteType.THERAPY_INTAKE:
      return item.sol_therapy_intake_note ?? "";
    case NoteType.THERAPY_FOLLOW_UP_SOAP:
      return item.sol_therapy_followup_note ?? "";
    case NoteType.THERAPY_FOLLOW_UP_DAP:
      return item.sol_therapy_followup_note ?? "";
    case NoteType.PSYCHIATRIC_FOLLOW_UP_SOAP:
      return item.psych_followup_note ?? "";
    case NoteType.SOAP_SPEECH_THERAPY:
      return item.document ?? "";
    case NoteType.CONSULT_NOTE:
      return item.document ?? "";
    default:
      // Check if item.document is empty or null
      return item.document && item.document.trim() !== "" ? item.document : "No information available";
  }
};

type NoteName =
  | "SOAP"
  | "Psychiatric Intake"
  | "Psychiatric Followup (SOAP)"
  | "Psychiatric Followup"
  | "Therapy Intake"
  | "Therapy Followup (SOAP)"
  | "Therapy Followup (DAP)"
  | "Intake"
  | "EAP Intake"
  | "DAP"
  | "EMDR"
  | "SOAP (Speech Therapy)"
  | "Consult Note"
  | "From EHR";

const BADGE_STYLES: Record<NoteName, string> = {
  SOAP: "badge-soap",
  "Psychiatric Intake": "badge-soap",
  "Psychiatric Followup (SOAP)": "badge-soap",
  "Psychiatric Followup": "badge-soap",
  "Therapy Intake": "badge-soap",
  "Therapy Followup (SOAP)": "badge-soap",
  "Therapy Followup (DAP)": "badge-soap",
  Intake: "badge-intake",
  "EAP Intake": "badge-intake",
  DAP: "badge-dap",
  EMDR: "badge-emdr",
  "SOAP (Speech Therapy)": "badge-soap-speech",
  "Consult Note": "badge-dap",
  "From EHR": "badge-ehr-imported",
};

export const getNotesAndBadge = (notes_type: number) => {
  const name = getNotesType(notes_type) as NoteName;
  if (name) {
    const badgeStyle = BADGE_STYLES[name] || "";
    return { name, badgeStyle };
  }
  return { name: "", badgeStyle: "" };
};

export enum GENDER_TYPE {
  NEUTRAL = 0,
  MALE = 1,
  FEMALE = 2,
}

export enum CATEGORY {
  INDIVIDUAL = 0,
  RELATIONSHIP = 1,
  FAMILY = 2,
  GROUP = 3,
}

export enum REFER_TYPE {
  CLIENT = 0,
  PATIENT = 1,
  INDIVIDUAL = 2,
  MEMBER = 3,
  CLIENT_NAME = 4,
}

export enum LANGUAGE_TYPE {
  ENGLISH = 0,
  SPANISH = 1,
  KOREAN = 2,
  MANDARIN = 3,
  HINDI = 4,
  ARABIC = 5,
  PORTUGUESE = 6,
  PUNJABI = 7,
  FRENCH = 8,
}

export enum LOCATION_TYPE {
  TELEHEALTH = 0,
  IN_PERSON = 1,
}

export const isLockedNoteOfANonPayingUser = (note: TNotes, userInfo: UserInfoProps) => {
  // TODO: This would lock all the notes that were failed due to catastrophic issues for non paying users
  // when they hit the rate limit
  return note.status === 2 && !userInfo.is_paying && userInfo.total_recordings >= userInfo.free_recordings_limit;
};

export const shouldShowTreatmentPlan = (note: TNotes) => {
  if (
    note.note_type === NoteType.Intake ||
    note.note_type === NoteType.SOL_PSYCH_INTAKE ||
    note.note_type === NoteType.THERAPY_INTAKE ||
    note.note_type === NoteType.PSYCHIATRIC_INTAKE
  ) {
    return true;
  }
  // Custom templates: show if treatment plan exists or is being generated
  if (note.treatment_plan || note.treatment_plan_generating) {
    return true;
  }
  return false;
};

export const SOAP_FIELDS = [
  {
    title: "Subjective",
    object: "subject",
    analyticsField: NoteSection.SUBJECTIVE,
  },
  {
    title: "Objective",
    object: "objective",
    analyticsField: NoteSection.OBJECTIVE,
  },
  {
    title: "Assessment",
    object: "assessment",
    analyticsField: NoteSection.ASSESSMENT,
  },
  {
    title: "Plan",
    object: "plan",
    analyticsField: NoteSection.PLAN,
  },
];

export const DAP_FIELDS = [
  {
    title: "Data",
    object: "data",
    analyticsField: NoteSection.DATA,
  },
  {
    title: "Assessment",
    object: "assessment",
    analyticsField: NoteSection.ASSESSMENT,
  },
  {
    title: "Plan",
    object: "plan",
    analyticsField: NoteSection.PLAN,
  },
];

export const isCustomisedTemplate = (pk: number, noteTypeId: number) => {
  if (pk) return true;
  return noteTypeId > 13;
};
